import { useState, useMemo } from 'preact/hooks'
import { postBeaconTracking } from 'uses/useSearchspringTracking'
import { getPrice } from 'uses/useSearchspringUtils'
import { redirect } from 'helpers/utils'
import { getBadges, getProductSubtitle } from 'uses/useShopify'
import Badge from 'snippets/badge/badge'
import Image from 'snippets/image/image'
import PlpQuickAdd from 'snippets/plp-quick-add/plp-quick-add'
import LinkCustom from 'snippets/link-custom/link-custom'
import PriceCompare from '../price-compare/price-compare'
import { getVariantQuantity } from 'uses/useWholeSale'

function PlpCard({
  className = 'relative flex flex-col p-3 pb-4 border-b md:p-2 md:pb-6 xl:p-4 xl:pb-8 border-default lg:relative lg:overflow-hidden plp-card hover:outline hover:outline-2 hover:outline-black',
  title,
  subtitle,
  url,
  handle,
  suffix = '',
  imageUrl,
  alt = '',
  secondImageUrl,
  available,
  variants,
  options,
  tags = [],
  intellisuggestData,
  intellisuggestSignature,
  isMiniCart = false,
  inSuggestion = false,
  showQuickAdd = true,
  collectionClickTracking = null,
  beaconClickTracking = null,
  isHoverSwapImage = true,
  collectionHandle,
  metafields = {},
  unitPerCartons,
  priceType = 'default'
}) {
  const productUrl = window.SLS_STATE.templateName === 'collection' && collectionHandle ? `collections/${collectionHandle}/products` : 'products'
  const isPreOrder = tags.some((item) => item === 'preorder')
  const isPreOrderMember = tags.some((item) => item === 'preorder:member')
  const isFindOutMore = tags.some((item) => item === 'find-out-more') || (suffix && suffix === 'build-your-own')
  const isComingSoon = tags.some((item) => item === 'coming-soon')
  const isUnavailableOnline = tags.some((item) => item === 'unavailable-online')
  const hasSecondImage = isHoverSwapImage && secondImageUrl && secondImageUrl.length > 1
  const isOnlyVariantDefault = variants.length === 1 && variants[0].title === 'Default Title'

  const badges = getBadges(tags)

  const getCurrentVariantAvailable = (variants, unitPerCartons) => {
    if (!variants || variants.length === 0) {
      return {}
    }
    return variants.find((variant, index) => getVariantQuantity(variant, unitPerCartons[index]) > 0) || variants[0]
  }

  const [isHover, setIsHover] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [currentVariant, setCurrentVariant] = useState(getCurrentVariantAvailable(variants, unitPerCartons))

  const isProductTaxable = useMemo(() => currentVariant.taxable === true, [currentVariant])

  const totalPrice = useMemo(() => getPrice(currentVariant.companyPriceFormatted, priceType) * unitPerCartons[currentVariant?.position ? currentVariant.position - 1 : 0], [currentVariant])

  const setPathUrl = () => localStorage.setItem('return_to', `/products/${handle}`)

  const renderButton = () => {
    if (!showQuickAdd || inSuggestion) {
      return
    }
    if (isFindOutMore) {
      return <LinkCustom link={`/${productUrl}/${handle}`} className="px-2 mt-3 button-add-to-bag md:text-base" text="Find out more" />
    } else if (isPreOrderMember && !window.SLS_STATE.customer.logged) {
      return (
        <LinkCustom
          link={`/account/login/?handle=${handle}&return_to=/products/${handle}`}
          onClick={setPathUrl}
          className="px-2 mt-3 button-add-to-bag md:text-base"
          text="Sign in to pre-order"
        />
      )
    } else if (isPreOrder || isPreOrderMember) {
      return <LinkCustom link={`/${productUrl}/${handle}`} className="px-2 mt-3 button-add-to-bag md:text-base" text="Pre-order" />
    } else if (isComingSoon) {
      return <LinkCustom link={`/${productUrl}/${handle}`} className="px-2 mt-3 button-add-to-bag md:text-base" text="Coming soon" />
    } else if (isUnavailableOnline) {
      return <LinkCustom link={`/${productUrl}/${handle}`} className="px-2 mt-3 button-add-to-bag md:text-base" text="Notify me" />
    } else {
      return (
        showQuickAdd && (
          <PlpQuickAdd
            isMiniCart={isMiniCart}
            variants={variants}
            available={available}
            options={options}
            metafields={metafields}
            unitPerCartons={unitPerCartons}
            currentVariant={currentVariant}
            setCurrentVariant={setCurrentVariant}
            totalPrice={totalPrice}
            isOnlyVariantDefault={isOnlyVariantDefault}
            productUrl={`/${productUrl}/${handle}`}
            isGst={isProductTaxable}
          ></PlpQuickAdd>
        )
      )
    }
  }

  const onTracking = async (e) => {
    e.preventDefault()
    sessionStorage.setItem('scrollPos', window.scrollY)
    if (intellisuggestData && intellisuggestSignature && collectionClickTracking) {
      collectionClickTracking(url, intellisuggestData, intellisuggestSignature)
      redirect(`${productUrl}`, handle, e.ctrlKey)
    } else if (beaconClickTracking) {
      const result = await postBeaconTracking(beaconClickTracking)
      if (result) {
        redirect(`${productUrl}`, handle, e.ctrlKey)
      }
    } else {
      redirect(`${productUrl}`, handle, e.ctrlKey)
    }
  }

  const showSecondImage = () => {
    if (!hasSecondImage) return
    setIsLoaded(true)
    setIsHover(true)
  }

  const hideSecondImage = () => {
    setIsHover(false)
  }

  return (
    <div className={className} onMouseEnter={() => showSecondImage()} onMouseLeave={() => hideSecondImage()}>
      <div className="relative mb-5 md:mb-7 plp-card-image">
        <a className={'cy-product-card'} href={`/${productUrl}/${handle}`} title={title} onClick={onTracking}>
          <Image
            imageUrl={imageUrl}
            alt={alt}
            className="aspect-[1/1]"
            imageClass="w-full h-full"
            imageBreakpoints="(min-width: 768px)|(min-width: 0px)"
            isCover={false}
            isLazy={true}
            key={handle}
          />
          {isLoaded && hasSecondImage && (
            <Image
              imageUrl={[secondImageUrl[1]]}
              alt={alt}
              className={`!absolute top-0 left-0 w-full aspect-[1/1] bg-white transition-opacity duration-300 ease-[ease] opacity-0 ${
                isHover ? '!opacity-100' : ''
              }`}
              imageClass="w-full h-full rounded-2xl overflow-hidden"
              imageBreakpoints="(min-width: 768px)|(min-width: 0px)"
              isCover={false}
              isLazy={true}
            />
          )}
        </a>
        <Badge
          items={badges}
          ignoreList={window.SLS_STATE.customBadge.ignore}
          className="absolute flex flex-wrap justify-center w-full gap-2 pointer-events-none -bottom-3 plp-card-badge"
        />
      </div>
      <div className="flex-1 mb-1 text-center md:px-4 plp-card-title">
        <h3>
          <a
            href={`/${productUrl}/${handle}`}
            title={title}
            className="font-bold !tracking-normal text-grey-900 text-label md:text-base plp-card-title-link"
            onClick={onTracking}
            dangerouslySetInnerHTML={{ __html: title }}
          ></a>
        </h3>
      </div>
      <div className="flex flex-col md:px-4 plp-card-content">
        {!inSuggestion && <span className="mb-1 tracking-normal text-center text-label text-grey-500 md:text-sm plp-card-variant">{currentVariant.sku}</span>}
        <span className="mb-1 tracking-normal text-center text-label text-grey-500 md:text-sm plp-card-variant">{getProductSubtitle(subtitle)}</span>
        <PriceCompare
          price={getPrice(currentVariant.companyPriceFormatted, priceType)}
          customPrice={getPrice(currentVariant.price, priceType)}
          className="mt-3 plp-card-price"
          classPrice="text-base md:text-lg"
          classCustomPrice="text-base md:text-lg"
          classLabel="text-label text-grey-500 tracking-normal"
          priceLabel={`Per Unit ${isProductTaxable ? '(Inc. GST)' : ''}`}
          customPriceLabel={`RRP ${isProductTaxable ? '(Inc. GST)' : ''}`}
        />
        {renderButton()}
      </div>
    </div>
  )
}

export default PlpCard
